import React, { useState } from "react";
import {
  Row,
  Col,
  Form,
  Input,
  Button,
  Card,
  CardBody,
  Spinner,
  Alert,
  Table,
} from "reactstrap";
import ComponentCard from "./ComponentCard";
import {
  startUserVmAPI,
  getUserVmStatusAPI,
} from "../../service/ManageUserVMsService";
import "../../App.css";

const authService = require("../../service/AuthService");

const pad2 = (n) => String(n).padStart(2, "0");
function formatUtc(dt) {
  if (!dt) return "";
  const d = new Date(dt);
  if (isNaN(d.getTime())) return dt;
  const mm = pad2(d.getUTCMonth() + 1);
  const dd = pad2(d.getUTCDate());
  const yyyy = d.getUTCFullYear();
  const hh = pad2(d.getUTCHours());
  const min = pad2(d.getUTCMinutes());
  return `${mm}-${dd}-${yyyy} ${hh}:${min} UTC`;
}

export default function ManageUserVMs() {
  const sessionUser = authService.getUser?.() || {};
  const defaultOrg = (sessionUser?.organization || "").trim().toLowerCase();

  // --- FORM INPUTS ---
  const [targetOrg, setTargetOrg] = useState(defaultOrg);
  const [targetUser, setTargetUser] = useState("");
  const [instanceName, setInstanceName] = useState("");
  const [acceptCosts, setAcceptCosts] = useState(false);

  const [acceptCostsErr, setAcceptCostsErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: "", msg: "" });

  // --- VM STATUS ---
  const [vmStatus, setVmStatus] = useState(null);
  const [statusError, setStatusError] = useState("");
  const [statusLoading, setStatusLoading] = useState(false);

  const validateAcceptCosts = (val) => {
    if (!val) return "You must acknowledge that this will consume credits / incur cloud costs.";
    return "";
  };

  const formValid = targetOrg && targetUser && instanceName && acceptCosts;

  /* --------------------- Submit: Start VM --------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: "", msg: "" });
    setAcceptCostsErr("");

    if (!targetOrg || !targetUser || !instanceName) {
      setStatus({
        type: "danger",
        msg: "Organization, username, and instance name are required.",
      });
      return;
    }

    const costErr = validateAcceptCosts(acceptCosts);
    if (costErr) {
      setAcceptCostsErr(costErr);
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        action: "startUserVm",
        organization: targetOrg.trim().toLowerCase(),
        requestedBy: targetUser.trim().toLowerCase(),
        instanceName: instanceName.trim(), // backend should use this as EC2 Name + tc_user_vms.instance_name
      };

      const resp = await startUserVmAPI(payload);

      setStatus({
        type: "success",
        msg:
          resp?.message ||
          "VM creation started. Use the Refresh button below to check status.",
      });

    } catch (err) {
      setStatus({
        type: "danger",
        msg:
          err?.response?.data?.message ||
          err?.message ||
          "Failed to create VM.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  /* --------------------- Refresh Status --------------------- */
  const handleRefreshStatus = async () => {
    setStatusError("");
    setVmStatus(null);

    if (!targetOrg || !targetUser) {
      setStatusError("Organization and username are required to fetch status.");
      return;
    }

    try {
      setStatusLoading(true);

      const resp = await getUserVmStatusAPI({
        action: "getUserVmStatus",
        organization: targetOrg.trim().toLowerCase(),
        requestedBy: targetUser.trim().toLowerCase(),
      });

      setVmStatus(resp || {});
      if (!resp?.instanceId) {
        setStatusError(
          resp?.message ||
            "No VM mapping found for this user/organization. It may still be starting or already terminated."
        );
      }

    } catch (err) {
      setStatusError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to fetch status."
      );
    } finally {
      setStatusLoading(false);
    }
  };

  return (
    <div className="page-wrapper overflow-hidden">
      {/* ---------- CREATE VM CARD ---------- */}
      <Row className="justify-content-center muc-container">
        <Col sm="12" md="10" lg="8">
          <ComponentCard>
            <h4 className="muc-title">Create New User VM</h4>

            {status.msg && (
              <Alert
                color={status.type}
                isOpen={!!status.msg}
                toggle={() => setStatus({ type: "", msg: "" })}
              >
                {status.msg}
              </Alert>
            )}

            <Card className="muc-card">
              <CardBody className="muc-card-body">
                <Form onSubmit={handleSubmit}>
                  {/* Organization */}
                  <div className="muc-field-row">
                    <div className="muc-label-cell">Organization:</div>
                    <div>
                      <Input
                        className="muc-input"
                        value={targetOrg}
                        onChange={(e) => setTargetOrg(e.target.value)}
                        placeholder="e.g. techcomb"
                      />
                    </div>
                  </div>

                  {/* Username */}
                  <div className="muc-field-row">
                    <div className="muc-label-cell">Assign to User:</div>
                    <div>
                      <Input
                        className="muc-input"
                        value={targetUser}
                        onChange={(e) => setTargetUser(e.target.value)}
                        placeholder="e.g. shreya"
                      />
                    </div>
                  </div>

                  {/* Instance Name */}
                  <div className="muc-field-row">
                    <div className="muc-label-cell">Instance Name:</div>
                    <div>
                      <Input
                        className="muc-input"
                        value={instanceName}
                        onChange={(e) => setInstanceName(e.target.value)}
                        placeholder="e.g. training-gpu-01"
                      />
                    </div>
                  </div>

                  {/* Credits / Cost acknowledgement */}
                  <div className="muc-field-row">
                    <div className="muc-label-cell">Credits / Cost:</div>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <Input
                        type="checkbox"
                        checked={acceptCosts}
                        onChange={(e) => {
                          setAcceptCosts(e.target.checked);
                          if (acceptCostsErr) {
                            setAcceptCostsErr(
                              validateAcceptCosts(e.target.checked)
                            );
                          }
                        }}
                        onBlur={() =>
                          setAcceptCostsErr(
                            validateAcceptCosts(acceptCosts)
                          )
                        }
                        style={{ width: "18px", height: "18px" }}
                      />
                      <span
                        style={{
                          marginLeft: 10,
                          fontSize: "12px",
                          color: "#666",
                        }}
                      >
                        I understand this will start a cloud VM and consume
                        credits / incur cloud charges while it is running.
                      </span>
                    </div>
                    {acceptCostsErr && (
                      <div
                        style={{
                          color: "#dc3545",
                          fontSize: "12px",
                          marginTop: 4,
                        }}
                      >
                        {acceptCostsErr}
                      </div>
                    )}
                  </div>

                  <div className="muc-submit-bar">
                    <Button
                      className="submit-action-button muc-submit-btn"
                      disabled={submitting || !formValid}
                      type="submit"
                    >
                      {submitting ? (
                        <>
                          <Spinner size="sm" style={{ marginRight: 8 }} />
                          Creating...
                        </>
                      ) : (
                        "Create VM"
                      )}
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </ComponentCard>
        </Col>
      </Row>

      {/* ---------- STATUS CARD ---------- */}
      <Row className="justify-content-center muc-container">
        <Col sm="12" md="10" lg="8" style={{ marginTop: 40 }}>
          <ComponentCard>
            <h4 className="muc-title">VM Status</h4>

            {statusError && (
              <Alert
                color="danger"
                isOpen={!!statusError}
                toggle={() => setStatusError("")}
              >
                {statusError}
              </Alert>
            )}

            <Card className="muc-card">
              <CardBody className="muc-card-body">
                <Button
                  className="submit-action-button muc-submit-btn"
                  disabled={statusLoading}
                  onClick={handleRefreshStatus}
                  type="button"
                >
                  {statusLoading ? (
                    <>
                      <Spinner size="sm" style={{ marginRight: 8 }} />
                      Checking...
                    </>
                  ) : (
                    "Refresh"
                  )}
                </Button>

                <div style={{ marginTop: 28 }}>
                  {vmStatus && vmStatus.instanceId && (
                    <Table
                      bordered
                      hover
                      size="md"
                      style={{
                        width: "100%",
                        minWidth: "600px",
                        tableLayout: "auto",
                      }}
                    >
                      <tbody>
                        <tr>
                          <th>Instance ID</th>
                          <td>{vmStatus.instanceId}</td>
                        </tr>
                        <tr>
                          <th>Instance Name</th>
                          <td>{vmStatus.instanceName || "—"}</td>
                        </tr>
                        <tr>
                          <th>State</th>
                          <td>{vmStatus.state || "unknown"}</td>
                        </tr>
                        <tr>
                          <th>Public IP</th>
                          <td>{vmStatus.publicIpAddress || "—"}</td>
                        </tr>
                        <tr>
                          <th>DNS</th>
                          <td>{vmStatus.publicDnsName || "—"}</td>
                        </tr>
                        <tr>
                          <th>Launch Time</th>
                          <td>{formatUtc(vmStatus.launchTime)}</td>
                        </tr>
                        <tr>
                          <th>Last Checked</th>
                          <td>{formatUtc(vmStatus.lastChecked)}</td>
                        </tr>
                        <tr>
                          <th>Message</th>
                          <td>{vmStatus.message || "—"}</td>
                        </tr>
                      </tbody>
                    </Table>
                  )}
                </div>
              </CardBody>
            </Card>
          </ComponentCard>
        </Col>
      </Row>
    </div>
  );
}
